import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useWidget } from '@telescope/cassini-hooks';
import { Prize, useSpinToWin, REELS_COUNT } from 'features';
import { SlotMachineOpen } from 'types';
import { ROUTES } from 'utils';
import { ErrorType } from 'types';
import { useNavigate } from 'react-router-dom';
export interface SlotMachineContextValue {
  prizes: Prize[]
  targets: any
}

const SlotMachineContext = createContext<SlotMachineContextValue | undefined>(undefined);

export const SlotMachineProvider = ({ children }: { children: React.ReactElement }) => {
  const { data: slotMachine } = useWidget({ select: (data: SlotMachineOpen) => data.snapshot.snapshot_views.slotMachine });
  const { winningPrizes } = slotMachine!;

  const prizes = useMemo(() => (
    Array(Math.ceil(10 / winningPrizes.length)).fill(winningPrizes).flat(1)
  ), [])

  const { prizeId, isAnimating } = useSpinToWin();

  const navigate = useNavigate();

  const getRandomIndex = (n: number) => Math.floor(Math.random() * n);

  const getTargets = useCallback(() => {
    const index = prizes.findIndex((p) => {
      return prizeId? prizeId == p.id : !p.id;
    })

    // If prize id is set but no matching prize is found, handle error
    if (prizeId && index === -1) {
      navigate(`${ROUTES.ERROR}/${ErrorType.GENERIC}`);
    }

    // If no prize id, select random indexes 
    // Losing target array requires at least one index with a unique prize id
    if (!prizeId && index === -1) {
      return Array.from(Array(REELS_COUNT)).reduce((acc, _, i) => {
        let isWinning = true;
        let randomTarget = getRandomIndex(prizes.length);

        const prevIndex = acc[i-1]; 
        if (i && prizes[prevIndex].id !== prizes[randomTarget].id) {
          isWinning = false
        }

        if (i === REELS_COUNT - 1 && isWinning) {
          const firstIndex = acc[0];
          while (prizes[randomTarget].id === prizes[firstIndex].id) {
            randomTarget = getRandomIndex(prizes.length)
          }
        }
 
        acc.push(randomTarget);
        return acc;

      }, [])
    }

    return Array(REELS_COUNT).fill(index);
  }, [prizes, prizeId])

  const [targets, setTargets] = useState<number>(getTargets());

  useEffect(() => {
    if (!isAnimating) return;

    const values = getTargets()
    setTargets(values);
  }, [isAnimating, prizeId])

  const value = useMemo(() => ({
    prizes,
    targets
  }), [
    prizes,
    targets
  ]);

  return <SlotMachineContext.Provider value={value}>{children}</SlotMachineContext.Provider>;
};

export function useSlotMachine() {
  const context = useContext(SlotMachineContext);

  if (context === undefined) {
    throw new Error('useSlotMachine must be used within a SlotMachineProvider');
  }

  return context;
}